import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  MenuItem,
  Select,
  Box,
  Stepper,
  StepButton,
  Step,
} from "@mui/material";
import { getIn, useFormikContext } from "formik";
import { StyledTextField } from "@components/CustomInput/customInput";
import { utilityCrossingListThunk } from "../redux/thunk/sideRoadwaysThunk";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@store/store";
import { WhetherSufficientDialog } from "./QuestionsModal";

type Field = {
  name: string;
  title: string;
  type: "text" | "radio" | "select";
  dataType?: "number";
  options?: string[];
  nestedFields?: Field[];
  alwaysShowNested?: boolean;
  validation?: {
    min?: number;
    max?: number;
    message?: string;
  };
};

type Section = {
  title: string;
  fields: Field[];
};

interface UtilityFormRendererProps {
  sections: Section[];
  handleFieldChange: (name: string, value: any) => void;
}

interface HandleStepClick {
  (index: number): void;
}

export const UtilityFormRenderer: React.FC<UtilityFormRendererProps> = ({
  sections,
  handleFieldChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { values, handleChange, setFieldValue, touched, errors, handleBlur } =
    useFormikContext<any>();
  const [activeStep, setActiveStep] = useState(0);
  const [crossingArrangementOptions, setCrossingArrangementOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [showWhetherSufficientDialog, setWhetherSufficientDialog] =
    useState(false);

  // Fetch crossing arrangement options when component mounts
  useEffect(() => {
    const fetchCrossingArrangementOptions = async () => {
      const hasCrossingArrangementField = sections.some((section) =>
        section.fields.some(
          (field) => field.name === "utility_crossing_arrangement_id",
        ),
      );

      if (hasCrossingArrangementField) {
        setLoadingOptions(true);
        try {
          const response = await dispatch(utilityCrossingListThunk());

          if (response.payload && response.payload.data) {
            const data = response.payload.data;
            if (Array.isArray(data)) {
              // Store the full objects with id and name
              setCrossingArrangementOptions(data);
            }
          } else {
            console.error("Failed to fetch crossing arrangement options");
          }
        } catch (error) {
          console.error("Error fetching crossing arrangement options:", error);
        } finally {
          setLoadingOptions(false);
        }
      }
    };

    fetchCrossingArrangementOptions();
  }, [sections, dispatch]);

  const handleStepClick: HandleStepClick = (index) => {
    setActiveStep(index);
    const sectionElement: HTMLElement | null = document.getElementById(
      `section-${index}`,
    );
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (values.width_of_vacant_row === "No") {
      setWhetherSufficientDialog(true);
    } else {
      setWhetherSufficientDialog(false);
    }
  }, [values.width_of_vacant_row]);

  // Calculate total land whenever left or right values change
  useEffect(() => {
    const leftValue = parseFloat(values.land_acquisition_proposal_left) || 0;
    const rightValue = parseFloat(values.land_acquisition_proposal_right) || 0;
    const total = leftValue + rightValue;

    // Only update if the calculated total is different from current value
    if (values.total_land_to_be_acquired !== total.toString()) {
      setFieldValue("total_land_to_be_acquired", total.toString());
      handleFieldChange("total_land_to_be_acquired", total.toString());
    }
  }, [
    values.land_acquisition_proposal_left,
    values.land_acquisition_proposal_right,
    setFieldValue,
    handleFieldChange,
    values.total_land_to_be_acquired,
  ]);

  const checkValidation = (field: Field, value: any): string | null => {
    if (!field.validation || !value) return null;

    // Automatically detect parent radio field with alwaysShowNested
    // Search through all sections to find if this field is nested under a radio with "No" selected
    const findParentRadio = (sections: Section[]): boolean => {
      for (const section of sections) {
        for (const parentField of section.fields || []) {
          if (
            parentField.type === "radio" &&
            parentField.alwaysShowNested &&
            parentField.nestedFields
          ) {
            // Check if current field is in this parent's nested fields
            const isNested = parentField.nestedFields.some(
              (nested: Field) => nested.name === field.name
            );
            if (isNested) {
              const parentValue = values?.[parentField.name];
              // If parent radio is "No", skip validation
              return parentValue === "No";
            }
          }
        }
      }
      return false;
    };

    // Check if parent is "No" in sections
    if (findParentRadio(sections)) {
      return null;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) return null;

    if (field.validation.min !== undefined && numValue < field.validation.min) {
      return field.validation.message || `At least ${field.validation.min}`;
    }

    if (field.validation.max !== undefined && numValue > field.validation.max) {
      return field.validation.message || `At most ${field.validation.max}`;
    }

    return null;
  };

  const renderField = (field: any, parent?: string) => {
    const fieldName = parent ? `${parent}.${field.name}` : field.name;
    const value = values?.[field.name];
    const error = getIn(errors, field.name);
    const touchedField = getIn(touched, field.name);

    // Check if field should be validated (only validate if parent is "Yes" or no parent)
    let shouldValidate = true;
    if (parent) {
      const parentValue = values?.[parent];
      shouldValidate = parentValue === "Yes";
    }

    const gridProps = {
      item: true,
      xs: 12,
      key: fieldName,
      sx: {
        mt: 1,
        mb: 1,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      },
    };

    return (
      <Grid container xs={12} key={fieldName} sx={{ mb: 0 }}>
        {/* TEXT FIELD */}
        {field.type === "text" && (
          <Grid {...gridProps} className={"grid-item-text"}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
              <Typography variant="h6" className="font-standard" sx={{ width: "95%" }}>
                {field.title}
              </Typography>
            </Box>
            <Box sx={{ width: "122px", flexShrink: 0 }}>
              <StyledTextField
                name={field.name}
                sx={{
                  width: "100%",
                  // Hide number arrows
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                    {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  },
                  // Make it visually distinct as read-only
                  ...(field.name === "total_land_to_be_acquired" && {
                    backgroundColor: "#f5f5f5",
                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                    },
                  }),
                }}
                className="input-field"
                type={field.type === "number" ? "text" : "text"}
                placeholder={
                  field.type === "number" ? "Enter number" : "Enter details"
                }
                value={values?.[field.name] || ""}
                onChange={(e) => {
                  let value = e.target.value;
                  if (field.type === "number" || field.dataType === "number") {
                    value = value.replace(/[^0-9.]/g, "");
                    // Ensure only one decimal point
                    const parts = value.split('.');
                    if (parts.length > 2) {
                      value = parts[0] + '.' + parts.slice(1).join('');
                    }
                  }

                  if (field.dataType === "string") {
                    value = value.replace(/[^a-zA-Z\s]/g, "");
                  }

                  if (
                    field.name === "utilized_nh_land_area" &&
                    /^0+$/.test(value)
                  ) {
                    value = "";
                  }

                  // Don't allow manual changes to total field
                  if (field.name !== "total_land_to_be_acquired") {
                    setFieldValue(field.name, value);
                    handleFieldChange(field.name, value);
                  }
                }}
                inputProps={{
                  ...(field.type === "number" || field.dataType === "number"
                    ? { inputMode: "decimal" }
                    : {}),
                  ...(field.name === "total_land_to_be_acquired" && {
                    readOnly: true,
                  }),
                }}
                onBlur={handleBlur}
                error={
                  Boolean(touchedField && error) ||
                  Boolean(checkValidation(field, value))
                }
                helperText={
                  touchedField && error ? error : checkValidation(field, value)
                }
              />
            </Box>
          </Grid>
        )}

        {/* RADIO FIELD */}
        {field.type === "radio" && (
          <>
            <Grid {...gridProps} className={"grid-item-radio"}>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                <Typography
                  variant="h6"
                  className="font-standard item-radio-title"
                  sx={{ width: "95%" }}
                >
                  {field.title}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <FormControl
                  error={touched[field.name] && Boolean(errors[field.name])}
                >
                  <RadioGroup
                    row
                    name={field.name}
                    value={values?.[field.name] ?? ""}
                    sx={{ flexWrap: "nowrap" }}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setFieldValue(field.name, selectedValue);
                      handleFieldChange(field.name, selectedValue);

                      if (field.nestedFields && selectedValue !== "Yes") {
                        field.nestedFields.forEach((nested: any) => {
                          setFieldValue(nested.name, "");
                          handleFieldChange(nested.name, "");
                        });
                      }
                    }}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio className="radio-button" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio className="radio-button" />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
                {/* Show error for radio */}
                {touchedField &&
                  typeof error === "string" &&
                  shouldValidate && (
                    <Typography
                      sx={{ color: "red", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {error}
                    </Typography>
                  )}
              </Box>
            </Grid>
            {/* Render nested fields - show always if alwaysShowNested, otherwise only if radio is 'Yes' */}
            {field.type === "radio" &&
              field.nestedFields &&
              (field.alwaysShowNested || values?.[field.name] === "Yes") && (
                <Grid
                  item
                  xs={12}
                  sx={{ pl: 3, borderLeft: "1px solid #e0e0e0", mt: 1 }}
                >
                  <Grid container spacing={0}>
                    {field.nestedFields.map((nested: any) =>
                      renderField(nested, field.name),
                    )}
                  </Grid>
                </Grid>
              )}
          </>
        )}

        {/* SELECT FIELD */}
        {field.type === "select" && (
          <Grid {...gridProps} className={"grid-item-select"}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
              <Typography variant="h6" className="font-standard" sx={{ width: "95%" }}>
                {field.title}
              </Typography>
            </Box>
            <Box sx={{ width: "122px", flexShrink: 0 }}>
              <FormControl
                fullWidth
                error={touched[field.name] && Boolean(errors[field.name])}
              >
                <Select
                  name={field.name}
                  value={values?.[field.name] || ""}
                  onBlur={handleBlur}
                  className="input-field"
                  size="small"
                  sx={{ borderRadius: 2 }}
                  onChange={(e) => {
                    setFieldValue(field.name, e.target.value);
                    handleFieldChange(field.name, e.target.value);
                  }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {loadingOptions &&
                      field.name === "utility_crossing_arrangement_id"
                      ? "Loading..."
                      : "Select"}
                  </MenuItem>

                  {/* For utility_crossing_arrangement_id - use id as value and name as display */}
                  {field.name === "utility_crossing_arrangement_id" &&
                    crossingArrangementOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}

                  {/* For other select fields - regular string options */}
                  {field.name !== "utility_crossing_arrangement_id" &&
                    (field.options || []).map((option: string) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                </Select>

                {/* Show error for select */}
                {touchedField &&
                  typeof error === "string" &&
                  shouldValidate && (
                    <Typography
                      sx={{ color: "red", fontSize: "0.75rem", mt: 0.5 }}
                    >
                      {error}
                    </Typography>
                  )}
              </FormControl>
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <>
      <Box pt={1}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 100,
            py: 0.8,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflowX: "auto",
              // textWrap: "nowrap",
              // whiteSpace: "nowrap",
              gap: 0,
              width: "100%",
            }}
          >
            {sections.map((section, i) => (
              <Box
                key={i}
                onClick={() => handleStepClick(i)}
                sx={{
                  position: "relative",
                  flex: 1,
                  marginInline: "8px",
                  height: "50px",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: activeStep === i ? "#3a1c73" : "#c4c4c4",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 600,
                  cursor: "pointer",
                  clipPath:
                    i === 0
                      ? "polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%)"
                      : "polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%, 20px 50%, 0% 0%)",
                  opacity: activeStep === i ? 1 : 0.7,
                  marginLeft: i === 0 ? "0" : "-20px",
                  paddingLeft: i === 0 ? "20px" : "30px",
                  paddingRight:
                    i === sections.length - 1 ? "20px" : "30px",
                  zIndex: activeStep === i ? 2 : 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    opacity: 0.9,
                  },
                }}
              >
                {section.title}
              </Box>
            ))}
          </Box>
        </Box>
        {sections.map((section, i) => (
          <Box
            key={`section-${i}`}
            mb={2}
            id={"section-" + i}
            className="section-box"
            sx={{ scrollMarginTop: "9vh" }}
          >
            <Typography
              className="font-bold head-xs"
              variant="h6"
              mb={2}
              sx={{
                fontSize: "1.5rem",
                fontWeight: "700",
                color: "#3a1c73",
                borderBottom: "2px solid #3a1c73",
                pb: 1,
              }}
            >
              {i + 1}. {section.title}
            </Typography>
            <Box
              className="section-fields"
              sx={{ width: "100%", overflow: "hidden" }}
            >
              {section.fields.map((field) => renderField(field))}
            </Box>
          </Box>
        ))}
      </Box>
      <WhetherSufficientDialog
        open={showWhetherSufficientDialog}
        onClose={() => setWhetherSufficientDialog(false)}
        onConfirm={() => {
          setWhetherSufficientDialog(false);
        }}
      />
    </>
  );
};
